import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Github, Star } from 'lucide-react';
import Badge from '../ui/Badge';
import StatusBadge from '../ui/StatusBadge';
import AppThumbnail from './AppThumbnail';
import EditAppButton from './EditAppButton';
import { useEditMode } from '../../contexts/EditModeContext';
import { getDetailPath } from '../../utils/appSchema';
import { formatMonth } from '../../utils/format';
import { trackEvent } from '../../utils/analytics';

/**
 * Thẻ ứng dụng trên trang chủ.
 *
 * Bấm vào thẻ sẽ mở TRANG GIỚI THIỆU của ứng dụng (`/app/:id`) chứ không nhảy
 * thẳng ra link ngoài — người dùng đọc giới thiệu, xem ảnh rồi mới quyết định mở.
 *
 * Khi admin đã đăng nhập, góc trên bên phải có thêm nút bút chì để sửa ngay tại
 * chỗ. Nút đó nằm NGOÀI thẻ <a> (button lồng trong link vừa sai HTML vừa dễ bấm
 * nhầm sang trang giới thiệu), nên phần khung ngoài mới là `group` để hiệu ứng
 * hover của thẻ vẫn chạy khi con trỏ đang ở trên nút.
 */
const AppCard = ({ app, index = 0 }) => {
    const [imageIndex, setImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const timerRef = useRef(null);
    const { canEdit } = useEditMode();

    const images = app.images?.length ? app.images : app.thumbnail ? [app.thumbnail] : [];
    const hasCarousel = images.length > 1;

    // Rê chuột lên thẻ thì ảnh tự chạy như một bản xem trước nhanh.
    useEffect(() => {
        if (isHovered && hasCarousel) {
            timerRef.current = setInterval(() => setImageIndex((prev) => (prev + 1) % images.length), 2500);
        } else {
            setImageIndex(0);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [isHovered, hasCarousel, images.length]);

    return (
        <div
            className="group relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Link
                to={getDetailPath(app)}
                onClick={() => trackEvent('app_card_click', 'apps', app.title)}
                className={`relative flex flex-col h-full bg-white/60 dark:bg-white/[0.06] backdrop-blur-glass border rounded-2xl overflow-hidden transition-all duration-300 group-hover:-translate-y-1.5 shadow-lg shadow-slate-900/5 dark:shadow-black/20 group-hover:shadow-xl ${
                    app.featured
                        ? 'border-primary-500/40 dark:border-primary-500/30 group-hover:border-primary-500/70 group-hover:shadow-primary-600/15'
                        : 'border-white/40 dark:border-white/[0.08] group-hover:border-white/60 dark:group-hover:border-white/[0.15] group-hover:shadow-slate-900/10 dark:group-hover:shadow-black/20'
                }`}
                style={{ animationDelay: `${index * 60}ms` }}
            >
                <div className="relative h-48 overflow-hidden">
                    {images.length > 0 ? (
                        images.map((src, i) => (
                            <img
                                key={src + i}
                                src={src}
                                alt={`${app.title} — ảnh ${i + 1}`}
                                loading="lazy"
                                className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                                    i === imageIndex ? 'opacity-100 scale-100 group-hover:scale-105' : 'opacity-0 scale-105'
                                }`}
                            />
                        ))
                    ) : (
                        <AppThumbnail app={app} />
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-white/90 dark:from-slate-950/90 via-transparent to-transparent opacity-70" />

                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        <Badge>{app.category}</Badge>
                        {app.featured && <Badge variant="accent" icon={Star}>Nổi bật</Badge>}
                    </div>

                    {app.githubUrl && (
                        <div className={`absolute top-4 ${canEdit ? 'right-[3.25rem]' : 'right-4'}`}>
                            <span className="w-7 h-7 rounded-full bg-slate-900/80 dark:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                                <Github className="w-3.5 h-3.5" />
                            </span>
                        </div>
                    )}

                    {hasCarousel && (
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/40 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {images.map((src, i) => (
                                <span
                                    key={src + i}
                                    className={`rounded-full transition-all duration-300 ${
                                        i === imageIndex ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white leading-snug group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                            {app.title}
                        </h3>
                        {app.status !== 'live' && <StatusBadge status={app.status} className="shrink-0 mt-0.5" />}
                    </div>

                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4 line-clamp-2">
                        {app.tagline || app.description}
                    </p>

                    {app.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                            {app.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="subtle">#{tag}</Badge>
                            ))}
                            {app.tags.length > 3 && <Badge variant="subtle">+{app.tags.length - 3}</Badge>}
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-auto pt-2">
                        {app.date ? (
                            <span className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 text-xs">
                                <Calendar className="w-3 h-3" />
                                {formatMonth(app.date)}
                            </span>
                        ) : (
                            <span />
                        )}
                        <span className="flex items-center gap-1.5 text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 text-sm font-medium transition-colors">
                            Xem chi tiết
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                    </div>
                </div>
            </Link>

            <EditAppButton app={app} className="absolute top-4 right-4 z-10" />
        </div>
    );
};

export default AppCard;
